import { parseAmount } from './amount.js';
import { requireNonEmptyString } from './ids.js';

const ACTION_BUTTON_GROUP_ORDER = [
    'payment',
    'statement',
    'pledges',
    'member',
    'management',
];

const ACTION_BUTTON_GROUPS = {
    payment: { label: 'Payment', icon: 'fa-solid fa-wallet' },
    statement: { label: 'Statement', icon: 'fa-regular fa-file-lines' },
    pledges: { label: 'Pledges', icon: 'fa-solid fa-list' },
    member: { label: 'Relationships', icon: 'fa-solid fa-link' },
    management: { label: 'Manage', icon: 'fa-solid fa-sliders' },
};

export default {
    fetchPledgeExpandContent(pledgeId, tableName) {
        const id = requireNonEmptyString(pledgeId, 'pledgeId');
        const route = requireNonEmptyString(this.pledgeExpandContentRoute, 'pledgeExpandContentRoute');
        const url = route.replace(':pledge_id', id);
        const table = tableName == null ? '' : String(tableName).trim();
        if (table !== '') {
            return $.get(url + '?table_name=' + encodeURIComponent(table));
        }
        return $.get(url);
    },

    fetchMemberExpandContent(key) {
        const url = this.memberExpandContentRoute?.replace(':member_id', key.id) ?? '';
        if (!url) return $.Deferred().reject(new Error('memberExpandContentRoute not set')).promise();
        return $.get(url);
    },

    createActionButtonsSection(key, $sourceRow) {
        const $section = $('<div>').addClass('action-buttons-section');
        const $container = $('<div>').addClass('action-buttons-container');
        $container.attr('id', `actionButtons[${key.type}-${key.id}]`);
        let buttons;

        if (key.type !== 'pledge') {
            const $row = $(`.clickable-row[data-member-id="${key.id}"]`);
            if ($row.length === 0) {
                return $section;
            }
            const memberData = this.getMemberDataFromRow($row);
            buttons = this.buildMemberActionButtons(key.id, memberData);
        } else {
            if (!$sourceRow?.length) {
                throw new Error('createActionButtonsSection: $sourceRow is required for pledge rows');
            }
            const pledgeData = this.resolvePledgeForActions(key.id, key.tableName, $sourceRow);
            if (!pledgeData) {
                return $section;
            }
            buttons = this.buildActionButtons(key.id, pledgeData);
        }

        this.appendGroupedActionButtons($container, buttons);
        $section.append($container);
        return $section;
    },

    appendGroupedActionButtons($container, buttons) {
        const groups = this.getActionButtonGroups(buttons);
        ACTION_BUTTON_GROUP_ORDER.forEach((groupId) => this.appendActionButtonGroup($container, groups, groupId));
        this.appendStatusBadges($container, groups.status);
    },

    getActionButtonGroups(buttons) {
        return buttons.reduce((groupMap, $button) => {
            const groupId = ($button.attr('data-action-group') || 'general').toString();
            (groupMap[groupId] ??= []).push($button);
            return groupMap;
        }, {});
    },

    appendStatusBadges($container, statusItems) {
        if (!statusItems || statusItems.length === 0) return;
        const $badgeWrap = $('<div>').addClass('action-status-badges');
        statusItems.forEach($item => $badgeWrap.append($item.addClass('action-status-badge')));
        $container.append($badgeWrap);
    },

    appendActionButtonGroup($container, groups, groupId) {
        const items = groups[groupId];
        if (!items || items.length === 0) return;
        const groupMeta = this.getActionButtonGroupMeta(groupId);
        const $group = $('<div>').addClass('action-buttons-group').attr('data-group', groupId);
        if (groupMeta.label) {
            const $label = $('<span>').addClass('action-buttons-group-label');
            if (groupMeta.icon) {
                $label.append($('<i>').addClass(groupMeta.icon).attr('aria-hidden', 'true'));
            }
            $label.append($('<span>').text(groupMeta.label));
            $group.append($label);
        }
        const $groupItems = $('<div>').addClass('action-buttons-group-items');
        items.forEach($button => $groupItems.append($button));
        $group.append($groupItems);
        $container.append($group);
    },

    getActionButtonGroupMeta(groupId) {
        return ACTION_BUTTON_GROUPS[groupId] || { label: '', icon: '' };
    },

    getUserInfoFromRow($row) {
        let status = 'Active';
        if ($row.data('congregation-archive') === 1) status = 'Archived';
        else if ($row.data('member-confirmed') !== 1) status = 'Membership Not Confirmed';
        return {
            name: $row.data('user-name') || '',
            email: $row.data('user-email') || '',
            phone: $row.data('user-phone') || '',
            status,
            pledge_total: parseAmount($row.data('pledge-total')),
            is_member: $row.data('is-member') === 1
        };
    },
};
